import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  Plus,
  Trash2,
  Send,
  ShieldCheck,
  Sparkles,
  AlertTriangle,
  Info,
  ArrowRight,
} from "lucide-react";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLang } from "@/context/LanguageContext";
import { CONTACT } from "@/lib/contact";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRODUCT_OPTIONS = [
  "Google Aufsteller – White Edition",
  "Google Aufsteller – Black Edition",
  "Instagram Aufsteller – Gradient Edition",
  "Facebook Aufsteller – Blue Edition",
  "TikTok Aufsteller – Neon Edition",
  "PayPal Aufsteller – Pay Edition",
  "WhatsApp Aufsteller – Chat Edition",
  "Custom Aufsteller – Wunsch-Plattform",
  "Andere",
];

const COPY = {
  de: {
    eyebrow: "Vielen Dank für Ihre Bestellung",
    title: "Ihre Bestellung ist bei uns angekommen.",
    intro:
      "Damit wir Ihre Aufsteller individuell für Sie konfigurieren können, benötigen wir noch ein paar kurze Angaben. Sie haben gerade einen Kauf abgeschlossen – die Abbuchung erfolgt durch CopeCart. Tragen Sie unten den Bestellnamen sowie die gewünschten Profile ein.",
    copecartHint:
      "Die Abbuchung erfolgt durch CopeCart. Sie erhalten Ihre Rechnung direkt von dort.",
    nameLabel: "Vor- und Nachname",
    nameHint: "Bitte geben Sie den Namen an, mit dem Sie Ihre Bestellung aufgegeben haben.",
    namePlaceholder: "z. B. Max Mustermann",
    signsTitle: "Ihre Schilder / Produkte",
    signsBody:
      "Tragen Sie pro Schild ein Produkt und das gewünschte Profil oder den Profil-Link ein. Sie können beliebig viele Schilder hinzufügen.",
    productLabel: "Produkt / Plattform",
    productPlaceholder: "Bitte wählen",
    profileLabel: "Profilname oder Profil-Link",
    profilePlaceholder: "z. B. @in7sekunden oder https://g.page/r/abc",
    addSign: "Weiteres Schild hinzufügen",
    removeSign: "Schild entfernen",
    submit: "Angaben übermitteln",
    submitting: "Wird gesendet …",
    requiredHint: "Pflichtfeld",
    backHome: "Zur Startseite",
    contactCard:
      "Sie haben noch eine Frage zu Ihrer Bestellung? Schreiben Sie uns kurz – wir melden uns zeitnah.",
    whatsapp: "WhatsApp öffnen",
    email: "E-Mail schreiben",
    success: {
      title: "Vielen Dank – Ihre Angaben sind gespeichert.",
      lead: "Wir konfigurieren Ihre Aufsteller jetzt entsprechend und melden uns, sobald sie versandt werden.",
      stepsTitle: "Status",
      saved: "Angaben gespeichert",
      savedBody: "Ihre Schilder und Profile sind sicher in unserem System hinterlegt.",
      notifyOk: "Benachrichtigung an den Betreiber technisch angenommen",
      notifyOkBody:
        "Die interne Benachrichtigung wurde zur Verarbeitung übergeben. Wir können noch keine garantierte Zustellung bestätigen.",
      notifyFail: "Benachrichtigung an den Betreiber konnte nicht bestätigt werden",
      notifyFailBody:
        "Ihre Daten sind dennoch gespeichert. Wir prüfen die Bestellung manuell. Bitte zur Sicherheit unten kurz Bescheid geben.",
      ref: "Referenz",
    },
    error:
      "Da ist etwas schiefgelaufen. Bitte versuchen Sie es erneut oder kontaktieren Sie uns über WhatsApp / E-Mail.",
  },
  en: {
    eyebrow: "Thank you for your order",
    title: "Your order has reached us.",
    intro:
      "To configure your stands individually we just need a few short details. You have just completed a purchase – billing is handled by CopeCart. Please enter the name on the order and the profiles you'd like to use.",
    copecartHint:
      "Note: Billing is handled by CopeCart. Your invoice is sent directly from there.",
    nameLabel: "First and last name",
    nameHint: "Please enter the name you used when placing the order.",
    namePlaceholder: "e.g. John Doe",
    signsTitle: "Your stands / products",
    signsBody:
      "For each stand, enter a product and the profile or profile link. You can add as many stands as you like.",
    productLabel: "Product / platform",
    productPlaceholder: "Please select",
    profileLabel: "Profile name or profile link",
    profilePlaceholder: "e.g. @in7sekunden or https://g.page/r/abc",
    addSign: "Add another stand",
    removeSign: "Remove stand",
    submit: "Submit details",
    submitting: "Sending …",
    requiredHint: "Required",
    backHome: "Back to home",
    contactCard:
      "Any question about your order? Drop us a short message – we'll get back quickly.",
    whatsapp: "Open WhatsApp",
    email: "Send email",
    success: {
      title: "Thank you – your details are saved.",
      lead: "We're configuring your stands now and will let you know as soon as they ship.",
      stepsTitle: "Status",
      saved: "Details saved",
      savedBody: "Your stands and profiles are securely stored in our system.",
      notifyOk: "Operator notification technically accepted",
      notifyOkBody:
        "The internal notification has been queued for processing. We cannot confirm guaranteed delivery yet.",
      notifyFail: "Operator notification could not be confirmed",
      notifyFailBody:
        "Your data is saved nonetheless. We will check the order manually. Please drop us a quick note via WhatsApp/email to be safe.",
      ref: "Reference",
    },
    error: "Something went wrong. Please try again or contact us via WhatsApp / email.",
  },
};

const blankItem = () => ({ product: "", profile: "" });

export default function Danke() {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.de;

  const [name, setName] = useState("");
  const [items, setItems] = useState([blankItem()]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const updateItem = (idx, field, value) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  };
  const addItem = () => setItems((prev) => [...prev, blankItem()]);
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const validate = () => {
    const e = { items: {} };
    if (!name.trim()) e.name = c.requiredHint;
    items.forEach((it, idx) => {
      if (!it.profile.trim()) {
        e.items[idx] = c.requiredHint;
      }
    });
    setErrors(e);
    return !e.name && Object.keys(e.items).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setErrorMsg("");
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        customer_name: name.trim(),
        language: lang,
        items: items.map((it) => ({
          product: it.product?.trim() || null,
          profile: it.profile.trim(),
        })),
      };
      const { data } = await axios.post(`${API}/order-confirmations`, payload, {
        timeout: 20000,
      });
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error(err);
      setErrorMsg(c.error);
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return <SuccessView result={result} c={c} />;
  }

  return (
    <div className="min-h-screen bg-white text-brand-ink font-body" data-testid="danke-page">
      <Header />
      <main className="pt-28 md:pt-36 pb-24">
        <div className="container-x max-w-3xl">
          <div className="rounded-3xl border border-brand-mist bg-brand-paper p-8 md:p-12">
            <div className="flex items-start gap-4">
              <span className="grid place-items-center h-11 w-11 rounded-2xl bg-brand-ink text-brand-champagne shrink-0">
                <Sparkles className="h-5 w-5" />
              </span>
              <div>
                <span className="overline">{c.eyebrow}</span>
                <h1 className="mt-3 text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-brand-ink text-balance">
                  {c.title}
                </h1>
              </div>
            </div>

            <p className="mt-6 text-base md:text-lg text-brand-graphite leading-relaxed text-pretty">
              {c.intro}
            </p>

            <div className="mt-6 rounded-2xl border border-brand-champagne/70 bg-brand-champagneSoft/30 p-4 flex items-start gap-3">
              <Info className="h-4 w-4 text-brand-graphite shrink-0 mt-0.5" />
              <p className="text-sm text-brand-graphite leading-relaxed">{c.copecartHint}</p>
            </div>
          </div>

          <form
            onSubmit={onSubmit}
            className="mt-8 rounded-3xl border border-brand-mist bg-white p-6 md:p-10 space-y-8"
            data-testid="danke-form"
            noValidate
          >
            <section>
              <Label htmlFor="customer_name" className="text-sm font-medium text-brand-ink">
                {c.nameLabel} <span className="text-brand-champagne">*</span>
              </Label>
              <p className="mt-1 text-xs text-brand-stone">{c.nameHint}</p>
              <Input
                id="customer_name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={c.namePlaceholder}
                autoComplete="name"
                className="mt-3 h-11 rounded-xl"
                data-testid="danke-name"
              />
              {errors.name && (
                <div className="mt-1.5 text-[12px] text-destructive flex items-center gap-1.5">
                  <AlertTriangle className="h-3 w-3" />
                  {errors.name}
                </div>
              )}
            </section>

            <div className="hairline" />

            <section>
              <div className="flex items-baseline justify-between gap-4 flex-wrap">
                <div>
                  <h2 className="font-heading text-xl md:text-2xl font-semibold tracking-tight text-brand-ink">
                    {c.signsTitle}
                  </h2>
                  <p className="mt-2 text-sm text-brand-graphite leading-relaxed max-w-xl">
                    {c.signsBody}
                  </p>
                </div>
                <span className="chip">{items.length}</span>
              </div>

              <div className="mt-6 space-y-4">
                {items.map((item, idx) => (
                  <div
                    key={idx}
                    className="rounded-2xl border border-brand-mist bg-brand-paper p-5 md:p-6"
                    data-testid={`danke-item-${idx}`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="text-xs uppercase tracking-[0.22em] font-semibold text-brand-stone">
                        Schild #{idx + 1}
                      </div>
                      {items.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeItem(idx)}
                          className="inline-flex items-center gap-1.5 text-xs text-brand-stone hover:text-destructive transition-colors"
                          data-testid={`danke-remove-${idx}`}
                          aria-label={c.removeSign}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          {c.removeSign}
                        </button>
                      )}
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label
                          htmlFor={`product-${idx}`}
                          className="text-xs font-medium text-brand-graphite"
                        >
                          {c.productLabel}
                        </Label>
                        <Select
                          value={item.product || ""}
                          onValueChange={(v) => updateItem(idx, "product", v)}
                        >
                          <SelectTrigger
                            id={`product-${idx}`}
                            className="mt-2 h-11 rounded-xl"
                            data-testid={`danke-product-${idx}`}
                          >
                            <SelectValue placeholder={c.productPlaceholder} />
                          </SelectTrigger>
                          <SelectContent>
                            {PRODUCT_OPTIONS.map((p) => (
                              <SelectItem key={p} value={p}>
                                {p}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label
                          htmlFor={`profile-${idx}`}
                          className="text-xs font-medium text-brand-graphite"
                        >
                          {c.profileLabel} <span className="text-brand-champagne">*</span>
                        </Label>
                        <Input
                          id={`profile-${idx}`}
                          value={item.profile}
                          onChange={(e) => updateItem(idx, "profile", e.target.value)}
                          placeholder={c.profilePlaceholder}
                          className="mt-2 h-11 rounded-xl"
                          data-testid={`danke-profile-${idx}`}
                        />
                        {errors.items && errors.items[idx] && (
                          <div className="mt-1.5 text-[12px] text-destructive flex items-center gap-1.5">
                            <AlertTriangle className="h-3 w-3" />
                            {errors.items[idx]}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={addItem}
                className="mt-5 inline-flex items-center gap-2 rounded-full border border-dashed border-brand-ink/30 px-5 py-3 text-sm font-medium text-brand-ink hover:border-brand-ink hover:bg-brand-cloud transition-all"
                data-testid="danke-add-item"
              >
                <Plus className="h-4 w-4" />
                {c.addSign}
              </button>
            </section>

            {errorMsg && (
              <div className="rounded-xl bg-destructive/10 border border-destructive/40 text-destructive p-4 flex items-start gap-3">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <p className="text-sm leading-relaxed">{errorMsg}</p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <Link
                to="/"
                className="text-xs uppercase tracking-[0.22em] font-semibold text-brand-stone hover:text-brand-ink transition-colors"
              >
                ← {c.backHome}
              </Link>
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="danke-submit"
              >
                {submitting ? c.submitting : c.submit}
                {!submitting && <Send className="h-4 w-4" />}
              </button>
            </div>
          </form>

          <ContactCard c={c} />
        </div>
      </main>
      <Footer />
    </div>
  );
}

function SuccessView({ result, c }) {
  const ok = result?.notification_status === "accepted";
  return (
    <div className="min-h-screen bg-white text-brand-ink font-body" data-testid="danke-success">
      <Header />
      <main className="pt-28 md:pt-36 pb-24">
        <div className="container-x max-w-3xl">
          <div className="rounded-3xl border border-brand-mist bg-brand-paper p-8 md:p-12">
            <div className="flex items-start gap-4">
              <span className="grid place-items-center h-12 w-12 rounded-2xl bg-brand-ink text-brand-champagne shrink-0">
                <CheckCircle2 className="h-6 w-6" />
              </span>
              <div>
                <span className="overline">{c.eyebrow}</span>
                <h1 className="mt-3 text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-brand-ink text-balance">
                  {c.success.title}
                </h1>
              </div>
            </div>
            <p className="mt-6 text-base md:text-lg text-brand-graphite leading-relaxed text-pretty">
              {c.success.lead}
            </p>
          </div>

          <section className="mt-8 rounded-3xl border border-brand-mist bg-white p-6 md:p-10">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <h2 className="font-heading text-lg md:text-xl font-semibold text-brand-ink tracking-tight">
                {c.success.stepsTitle}
              </h2>
              {result?.group_id && (
                <div className="text-xs text-brand-stone">
                  {c.success.ref}: <span className="font-mono">{result.group_id.slice(0, 8)}</span>
                </div>
              )}
            </div>

            <ul className="mt-6 space-y-4">
              <li className="flex items-start gap-3" data-testid="danke-status-saved">
                <span className="mt-0.5 grid place-items-center h-7 w-7 rounded-full bg-brand-ink text-brand-champagne shrink-0">
                  <CheckCircle2 className="h-4 w-4" />
                </span>
                <div>
                  <div className="text-sm font-medium text-brand-ink">{c.success.saved}</div>
                  <p className="text-sm text-brand-graphite leading-relaxed">
                    {c.success.savedBody}
                  </p>
                </div>
              </li>
              <li
                className="flex items-start gap-3"
                data-testid={`danke-status-notify-${ok ? "ok" : "fail"}`}
              >
                <span
                  className={`mt-0.5 grid place-items-center h-7 w-7 rounded-full shrink-0 ${
                    ok ? "bg-brand-ink text-brand-champagne" : "bg-brand-stone/20 text-brand-graphite"
                  }`}
                >
                  {ok ? <ShieldCheck className="h-4 w-4" /> : <Info className="h-4 w-4" />}
                </span>
                <div>
                  <div className="text-sm font-medium text-brand-ink">
                    {ok ? c.success.notifyOk : c.success.notifyFail}
                  </div>
                  <p className="text-sm text-brand-graphite leading-relaxed">
                    {ok ? c.success.notifyOkBody : c.success.notifyFailBody}
                  </p>
                </div>
              </li>
            </ul>
          </section>

          <ContactCard c={c} />

          <div className="mt-10 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-medium text-brand-ink hover:text-brand-graphite transition-colors"
            >
              {c.backHome}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function ContactCard({ c }) {
  return (
    <div className="mt-10 rounded-3xl border border-brand-mist bg-brand-cloud p-6 md:p-8">
      <p className="text-sm text-brand-graphite leading-relaxed">{c.contactCard}</p>
      <div className="mt-4 flex flex-wrap gap-3">
        <a
          href={CONTACT.whatsappUrl(
            "Hallo in7sekunden, ich habe gerade eine Bestellung über CopeCart abgeschlossen und eine Rückfrage."
          )}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary"
        >
          {c.whatsapp}
        </a>
        <a href={`mailto:${CONTACT.email}`} className="btn-ghost">
          {c.email}
        </a>
      </div>
    </div>
  );
}
